import { Badge } from "@/components/ui/badge";
import { motion } from "framer-motion";
import { Gauge, Hammer, TrendingUp } from "lucide-react";

const BENEFITS = [
    {
        Icon: Gauge,
        kicker: "01",
        title: "Simplicité d'utilisation",
        desc: "Aucune formation de 3 jours, pas de manuel de 80 pages. Vos collaborateurs comprennent en 5 minutes — c'est notre engagement le plus strict.",
        accent: "217 91% 60%",
        stats: ["< 5 min onboarding", "0 jargon", "UI minimaliste"],
    },
    {
        Icon: Hammer,
        kicker: "02",
        title: "Conçu par un expert du terrain",
        desc: "Pas une équipe produit hors-sol : Sébastien et son équipe utilisent chaque outil au quotidien dans leur propre agence. Vous bénéficiez de 20 ans de leçons.",
        accent: "258 89% 66%",
        stats: ["~20 ans d'expérience", "30-40 sites/an", "Itéré en interne"],
    },
    {
        Icon: TrendingUp,
        kicker: "03",
        title: "Impact direct sur la rentabilité",
        desc: "Gain de temps brut, automatisation des tâches répétitives, montée en valeur de vos prestations. Chaque outil est conçu pour faire grossir votre CA, pas pour briller en démo.",
        accent: "152 76% 52%",
        stats: ["+ heures facturables", "+ avis = + CA", "ROI sous 30j"],
    },
];

export default function BenefitsSection() {
    return (
        <section id="avantages" className="relative py-24 sm:py-32">
            <div className="pointer-events-none absolute inset-0 -z-10">
                <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
            </div>
            <div className="mx-auto max-w-7xl px-4 sm:px-6">
                <div className="flex flex-col items-start justify-between gap-6 lg:flex-row lg:items-end">
                    <div className="max-w-2xl">
                        <Badge variant="outline" className="rounded-full border-border bg-card/60 px-3 py-1 text-xs text-muted-foreground">
                            Pourquoi Web Me Up
                        </Badge>
                        <h2 className="mt-4 font-display text-3xl font-semibold leading-tight tracking-tight text-foreground sm:text-4xl lg:text-5xl">
                            Trois promesses. <span className="text-gradient">Tenues.</span>
                        </h2>
                    </div>
                    <p className="max-w-md text-sm text-muted-foreground sm:text-base">
                        On ne vend pas de la techno pour la techno. Chaque feature existe pour vous faire gagner du temps ou de l'argent — idéalement les deux.
                    </p>
                </div>

                <div className="mt-14 grid gap-5 lg:grid-cols-3">
                    {BENEFITS.map((b, i) => (
                        <BenefitCard key={b.title} benefit={b} index={i} />
                    ))}
                </div>
            </div>
        </section>
    );
}

const BenefitCard = ({ benefit, index }) => {
    const { Icon, kicker, title, desc, accent, stats } = benefit;
    return (
        <motion.article
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
            className="group relative flex flex-col overflow-hidden rounded-2xl border border-border bg-gradient-card p-7 transition-all duration-500 hover:-translate-y-1 hover:border-primary/40 hover:shadow-elegant"
        >
            <div
                className="absolute inset-x-0 top-0 h-px"
                style={{ background: `linear-gradient(90deg, transparent, hsl(${accent}), transparent)` }}
            />
            <div className="absolute -right-12 -top-12 h-40 w-40 rounded-full opacity-10 blur-3xl transition-opacity duration-500 group-hover:opacity-20" style={{ background: `hsl(${accent})` }} />

            <div className="flex items-center justify-between">
                <div
                    className="flex h-12 w-12 items-center justify-center rounded-xl"
                    style={{ background: `hsl(${accent} / 0.15)`, border: `1px solid hsl(${accent} / 0.3)` }}
                >
                    <Icon className="h-5 w-5" style={{ color: `hsl(${accent})` }} />
                </div>
                <span className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">{kicker}</span>
            </div>

            <h3 className="mt-6 font-display text-xl font-semibold text-foreground sm:text-2xl">{title}</h3>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{desc}</p>

            <div className="mt-auto flex flex-wrap gap-2 pt-6">
                {stats.map((s) => (
                    <span key={s} className="inline-flex items-center rounded-full border border-border bg-background/40 px-2.5 py-1 text-[11px] font-medium text-foreground/80">
                        {s}
                    </span>
                ))}
            </div>
        </motion.article>
    );
};
